import React from "react";
import { useSubject } from "../context/SubjectContext";
import { useCenter } from "../context/CenterContext";
import { useConsortiaCenter } from "../context/ConsortiaCenterContext";
import { Link } from "react-router-dom";
import "./SidePanel.css";
import { QUALIFICATION_NAME } from "../constants/Constants";
import { useApi } from "../context/ApiClient";
import { getCenterType } from "../utils/utils";
import { normalizeVisitArrangementStatus } from "../utils/normalizeVisitArrangementStatus";

const SidePanel = () => {
  const {
    subjects,
    setSubjectData,
    subjectData,
    setLoading,
    dashboardCache,
    setDashboardCache,
  } = useSubject();
  const { centerNumber } = useCenter();
  const { consortiaCentres = [] } = useConsortiaCenter();
  const api = useApi(process.env.REACT_APP_VAF_BASE_URL);


  const fetchSubjectDetails = async (subject) => {
    const currentCentreType = getCenterType(
      consortiaCentres,
      Number(centerNumber),
      subject?.subjectName,
      subject?.subjectId
    ) || '';


    try {
      // Return cached data if available
      if (dashboardCache[subject.subjectId]) {
        setSubjectData(dashboardCache[subject.subjectId]);
        return;
      }
      setLoading(true);
      const response = await api.get(
        !currentCentreType ? `getDashboardDetails/${centerNumber}/${subject.subjectId}` : `getConsortiaDashboardDetails/${centerNumber}/${subject.subjectId}/${currentCentreType}`
      );
      const data = normalizeVisitArrangementStatus(response.data)
      const newSubjectData = {
        selectedSubjectId: subject.subjectId,
        selectedSubject: subject.subjectName,
        selectedSubjectCode: subject.certificateCodes.join("/"),
        subjectDetails: data?.data[0],

        // When centre type is Lead
        ...(currentCentreType === "Lead" && {
          isConsortiaLeadCandidateRegistered:
            data?.data[0]?.isConsortiaLeadCandidateRegistered,
          isConsortiaRestCandidateRegistered:
            data?.data[0]?.isConsortiaRestCandidateRegistered,
          isLeadCandidateRegistered: data?.data[0]?.isLeadCandidateRegistered,
        }),

        // When centre type is Rest
        ...(currentCentreType === "Rest" && {
          isConsortiaRestCandidateRegistered:
            data?.data[0]?.isConsortiaRestCandidateRegistered,
          isRestCandidateRegistered: data?.data[0]?.isRestCandidateRegistered,
        }),

        // When centre type is null, empty, or undefined
        ...((!currentCentreType || currentCentreType.trim() === "") && {
          isRegistered: data?.data[0]?.isCandidateRegistered,
        }),
      };

      setSubjectData(newSubjectData);
      setDashboardCache((prev) => ({
        ...prev,
        [subject.subjectId]: newSubjectData,
      }));
    } catch (error) {
      console.log("Error fetching subject details:", error);

    } finally {
      setLoading(false); // end loading
    }
  };

  return (
    <aside className="subject-menu">
      <div id="pnlSideMenuNav">
        <hr className="mt-0 mb-2" />
        <ul className="menu-list">
          <li>
            <Link to="/">Home</Link>
          </li>
        </ul>
        <hr className="m-0 mb-2" />

        {/* AAQs section */}
        <ul className="menu-list">
          <li>
            <h5 className="title is-7 p-2">
              Cambridge Advanced Nationals (AAQs)
            </h5>
          </li>
          {subjects
            .filter(
              (subject) =>
                subject.qualificationShortName === QUALIFICATION_NAME.AAQS_SUBJECT_NAME 
            )
            .sort((a, b) =>
              a.subjectName
                .toLowerCase()
                .localeCompare(b.subjectName.toLowerCase())
            )
            .map((subject, index) => (
              <li key={index}>
                <Link
                  to="/dashboard"
                  onClick={(e) => {
                    const link = e.currentTarget;
                    //  Prevent re-fetch if same subject is clicked
                    if (subjectData?.selectedSubjectId === subject.subjectId) {
                      e.preventDefault(); // prevents a navigation re-paint
                      return;
                    }
                    // Apply focus manually so blue border shows
                    link.focus();

                    // Then remove focus after 1 second
                    setTimeout(() => {
                      link.blur();
                    }, 1000);
                    fetchSubjectDetails(subject);
                  }}
                  className={
                    subjectData?.selectedSubjectId === subject.subjectId
                      ? "active"
                      : ""
                  }
                >
                  {subject.subjectName}
                </Link>
              </li>
            ))}
        </ul>
        <hr className="m-0 mb-2" />

        {/* Enrichment Section */}
        <ul className="menu-list">
          <li>
            <h5 className="title is-7 p-2">Enrichment qualifications</h5>
          </li>
          {[...subjects]
            .filter((subject) =>
              QUALIFICATION_NAME.ENRICHMENT_SUBJECT_NAMES.includes(
                subject.qualificationShortName
              )
            )
            .sort((a, b) => a.subjectName.localeCompare(b.subjectName))
            .map((subject, index) => (
            <li key={index}>
              <Link
                to={subject.path}
                onClick={(e) => {
                  const link = e.currentTarget;
                  if (subjectData?.selectedSubjectId === subject.subjectId) {
                    e.preventDefault(); // prevents a navigation re-paint
                    return;
                  }
                  link.focus(); // triggers the blue border
                  setTimeout(() => {
                    link.blur(); // removes it after 1s
                  }, 1000);

                  fetchSubjectDetails(subject);
                }}
                className={
                  subjectData?.selectedSubjectId === subject.subjectId
                    ? "active"
                    : ""
                }
              >
                {subject.subjectName}
              </Link>
            </li>
          ))}
        </ul>
        <hr className="m-0 mb-2" />
      </div>
    </aside>
  );
};

export default SidePanel;